import { useFooterSaveState, type FooterSaveState } from '../../components/settings/footerSave';
import { useUnsavedChangesGuard } from '../../components/settings/useUnsavedChangesGuard';
import { EDIT_UNSAVED_MESSAGE } from '../brand/brandCopy';
import { brandLeaveBlocks } from '../brand/leaveRules';

/** Outside a FooterSaveProvider nothing is ever unsaved. */
const NOTHING_UNSAVED: FooterSaveState = {
  dirty: false,
  canSave: false,
  saving: false,
  status: 'clean',
  saveAll: () => Promise.resolve(true),
  discardAll: () => undefined,
  held: false,
  settleHeld: () => Promise.resolve(true),
};

/**
 * The one leave guard of an Edit Brand Workspace page (spec:198; web.md §2.3, §6.1): R1's guard over
 * the page's footer, asking with the Edit page's sentence before the page is left with unsaved
 * changes: another page in the side menu, Brand Setup, anywhere else in the app, or a switch to another
 * workspace (`?ws=a` to `?ws=b`, brandLeaveBlocks). Keep editing stays; Discard changes puts back the
 * saved values and goes; Save and continue saves every card and goes only if all of them saved.
 * Closing or reloading the tab gets the browser's own prompt. One per page: react-router keeps a single
 * active blocker. Rendered inside the page's FooterSaveProvider, BEFORE the page's content, so its
 * blocker takes a new state before any effect of the content navigates.
 */
export function EditPageGuard() {
  const footer = useFooterSaveState() ?? NOTHING_UNSAVED;
  const guard = useUnsavedChangesGuard(footer.dirty, {
    save: footer.saveAll,
    discard: footer.discardAll,
    canSave: footer.canSave,
    saving: footer.saving,
    message: EDIT_UNSAVED_MESSAGE,
    held: footer.held,
    settleHeld: footer.settleHeld,
    shouldBlock: brandLeaveBlocks,
  });
  return <>{guard}</>;
}
